import { Tabs, TabsList, TabsTrigger } from '@/components/_ui/Tabs'
import {
  getPickerRegions,
  languageTranslations,
} from '@/constants/pickerRegions/regions'
import { useState } from 'react'
import T, { useTranslate } from '@/components/Helpers/Translate'
import Image from 'next/image'
import { Separator } from '@/components/_ui/Separator'
import { useHandleChangeLocale } from '@/lib/hooks/helpers/useHandleChangeLocale'
import type { SupportedAlpha2CountryCode } from '@/lib/utils/localization/getSupportedAlpha2CountryCodes'
import type { SupportedLanguage } from '@/lib/utils/localization/getSupportedLanguages'
import useGlobalStore from '@/store/useGlobalStore'
import { DesktopCountrySelectorHeader } from './DesktopCountrySelectorHeader'

type Region = keyof ReturnType<typeof getPickerRegions>

export const DesktopCountrySelector = () => {
  const { handleChangeLocale } = useHandleChangeLocale()
  const { app } = useGlobalStore()
  const alpha2 = app.alpha2 as SupportedAlpha2CountryCode

  const { translate } = useTranslate()

  const userRegion = Object.keys(getPickerRegions()).find((region) =>
    Object.keys(getPickerRegions()[region as Region]).includes(alpha2),
  )

  const [selectedRegion, setSelectedRegion] = useState<Region>(
    userRegion as Region,
  )

  return (
    <div className="w-screen max-w-[1200px]">
      <div className="w-ful p-5 pt-10">
        <DesktopCountrySelectorHeader />
        <div className="mt-10">
          <Tabs defaultValue={selectedRegion} className="w-[400px]">
            <TabsList>
              {Object.keys(getPickerRegions()).map((region: string) => {
                if (
                  Object.keys(getPickerRegions()[region as Region]).length === 0
                ) {
                  return null
                }

                return (
                  <TabsTrigger
                    className="px-10"
                    key={region}
                    value={region}
                    onClick={() =>
                      setSelectedRegion(
                        region as keyof ReturnType<typeof getPickerRegions>,
                      )
                    }
                  >
                    <T>{region}</T>
                  </TabsTrigger>
                )
              })}
            </TabsList>
          </Tabs>
          <Separator className="my-5" />
          <div className="my-10 grid grid-cols-4">
            {Object.keys(getPickerRegions()[selectedRegion])
              .sort((a, b) => translate(a).localeCompare(translate(b)))
              .map((country) => {
                // @ts-expect-error: accessing object with dynamic key
                const languages = getPickerRegions()[selectedRegion][
                  country
                ] as string[]

                return (
                  <div
                    className="mb-6 flex items-center space-x-2"
                    key={country}
                  >
                    <Image
                      className="pb-1"
                      src={`/flags/${country}.svg`}
                      alt="Country Flag"
                      height={30}
                      width={30}
                    />
                    <div>
                      <p
                        onClick={() => {
                          ;(async () => {
                            await handleChangeLocale({
                              newAlpha2: country as SupportedAlpha2CountryCode,
                              newLanguage: languages[0] as SupportedLanguage, // default to first language
                            })
                          })().catch(() => {})
                        }}
                        className="cursor-pointer text-primary hover:underline"
                      >
                        <T>{country}</T>
                      </p>
                      <div className="flex space-x-3">
                        {languages.map((language: string) => (
                          <span
                            className="cursor-pointer text-xs text-slate-500 hover:underline"
                            onClick={() => {
                              ;(async () => {
                                await handleChangeLocale({
                                  newAlpha2:
                                    country as SupportedAlpha2CountryCode,
                                  newLanguage: language as SupportedLanguage,
                                })
                              })().catch(() => {})
                            }}
                            key={language}
                          >
                            <T>
                              {
                                languageTranslations[
                                  language as keyof typeof languageTranslations
                                ]
                              }
                            </T>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )
              })}
          </div>
        </div>
      </div>
    </div>
  )
}
